import { TagFilter } from 'gql-types';
import { atom, useAtom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { useMemo } from 'react';
import { z } from 'zod';

import { atomWithLocalStorage } from '../utils/browserStorage';

export type SelectedTag = { id: string; name: string; groupId: string };

const selectedTagSchema = z.object({
    id: z.string(),
    name: z.string(),
    groupId: z.string(),
});

// Stores selected tags keyed by org ID so each org remembers its own selection.
const selectedTagsByOrgAtom = atomWithLocalStorage(
    'selectedTagsByOrg',
    {} as Record<string, SelectedTag[]>,
    z.record(z.string(), z.array(selectedTagSchema)),
);

// Derives a per-org atom for reading/writing that org's selected tags.
const selectedTagsForOrgAtom = atomFamily((orgId: string) =>
    atom(
        (get) => {
            const allTags = get(selectedTagsByOrgAtom);
            return orgId in allTags ? allTags[orgId] : [];
        },
        (get, set, value: SelectedTag[]) => {
            const prev = get(selectedTagsByOrgAtom);
            set(selectedTagsByOrgAtom, { ...prev, [orgId]: value });
        },
    ),
);

/**
 * Builds a TagFilter that ANDs across tag groups and ORs within each group.
 * e.g. selecting Arbor (parent) + AH302 (agency) produces:
 *   { id: { inList: [arborId] }, AND: { id: { inList: [ah302Id] } } }
 * so the patient must match at least one tag from each group.
 *
 * NOTE: groupId is persisted in localStorage and may go stale if the backend
 * transitions from virtual display groups to real ones. Tag *IDs* are
 * reconciled on popover open, but groupIds are not — this is acceptable
 * because the transition is a one-time event and re-selecting tags fixes it.
 */
export function buildTagFilter(selectedTags: SelectedTag[]): TagFilter | null {
    if (selectedTags.length === 0) return null;

    // OR across all selected tags — patient must have at least one of the selected tags.
    return { id: { inList: selectedTags.map((t) => t.id) } };
}

export const useSelectedTagFilter = (orgId: string) => {
    const [selectedTags, setSelectedTags] = useAtom(selectedTagsForOrgAtom(orgId));

    const tagFilter: TagFilter | null = useMemo(() => buildTagFilter(selectedTags), [selectedTags]);

    return { selectedTags, setSelectedTags, tagFilter };
};
